import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { RedactionOptions } from "../../../api/transcription";

interface PiiCheckboxRowProps {
  readonly label: string;
  readonly description: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}

function PiiCheckboxRow({ label, description, checked, onChange }: PiiCheckboxRowProps) {
  return (
    <Box
      component="label"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        cursor: "pointer",
        py: 0.25,
      }}
    >
      <Box
        component="input"
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
        sx={{ mt: "3px", accentColor: "var(--accent)", cursor: "pointer" }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "var(--text-primary)",
          }}
        >
          {label}
        </Box>
        <Box
          component="span"
          sx={{ fontSize: "0.6875rem", color: "var(--text-tertiary)" }}
        >
          {description}
        </Box>
      </Box>
    </Box>
  );
}

interface InlineRedactionOptionsProps {
  readonly variant: "optional" | "required";
  readonly isPending: boolean;
  readonly errorMessage?: string | null;
  readonly onSubmit: (options: RedactionOptions) => void;
  readonly onCancel: () => void;
}

export default function InlineRedactionOptions({
  variant,
  isPending,
  errorMessage,
  onSubmit,
  onCancel,
}: InlineRedactionOptionsProps) {
  const { t } = useTranslation("common");
  const [financial, setFinancial] = useState(false);
  const [personal, setPersonal] = useState(false);

  const title = variant === "required"
    ? t("redactTranscriptTitle")
    : t("transcriptionOptions");
  const intro = variant === "required"
    ? t("redactionRequiredIntro")
    : t("redactionOptionalIntro");

  const requestDisabled =
    isPending || (variant === "required" && !financial && !personal);

  const handleSubmit = () => {
    onSubmit({
      enabled: financial || personal,
      piiToRedact: { financial, personal },
    });
  };

  return (
    <Box
      sx={{
        borderRadius: "10px",
        border: "1px solid var(--border-mid)",
        overflow: "hidden",
        backgroundColor: "var(--bg-surface)",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid var(--border-subtle)",
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: "var(--text-primary)",
        }}
      >
        {title}
      </Box>

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Box
          sx={{
            fontSize: "0.75rem",
            color:
              variant === "required"
                ? "var(--signal-amber)"
                : "var(--text-secondary)",
            lineHeight: 1.5,
          }}
        >
          {intro}
        </Box>

        <PiiCheckboxRow
          label={t("financialPii")}
          description={t("financialPiiDescription")}
          checked={financial}
          onChange={setFinancial}
        />

        <PiiCheckboxRow
          label={t("personalPii")}
          description={t("personalPiiDescription")}
          checked={personal}
          onChange={setPersonal}
        />

        {errorMessage && (
          <Box
            sx={{
              fontSize: "0.75rem",
              color: "#FB7185",
            }}
          >
            {errorMessage}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 0.25,
          }}
        >
          <Box
            component="button"
            onClick={onCancel}
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 500,
              color: "var(--text-secondary)",
              background: "none",
              border: "1px solid var(--border-mid)",
              borderRadius: "6px",
              px: 1.5,
              py: 0.5,
              cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": { borderColor: "var(--text-secondary)" },
            }}
          >
            {t("cancel")}
          </Box>
          <Box
            component="button"
            disabled={requestDisabled}
            onClick={handleSubmit}
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "#fff",
              background: "var(--accent)",
              border: "none",
              borderRadius: "6px",
              px: 1.5,
              py: 0.5,
              cursor: requestDisabled ? "default" : "pointer",
              fontFamily: "inherit",
              opacity: requestDisabled ? 0.5 : 1,
              "&:hover": requestDisabled ? {} : { opacity: 0.88 },
            }}
          >
            {t("requestRedaction")}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
